import { get } from 'local-storage'

const environment = process.env.APP_ENV
const vercelEnvironment = process.env.NEXT_PUBLIC_VERCEL_ENV

type Config = {
  name: string
  version: string
  supportEmail: string
  e2eEmail: string
  environment: string
  vercelEnvironment: string
  service: string
  branch: string
  apiEndpoint: string
  pdfGeneratorEndpoint: string
  appHost: string
  signInUrl: string
  defaultPhoneCountryCode: string
  defaultLocale: string
  defaultTimezone: string
  defaultLanguage: string
  auth: {
    domain: string
    clientId: string
    redirectPath: string
    redirectUri: string
    audience: string
  }
  redirectAppUrl: string
  googleMapsAPIKey: string
  appLinks: {
    googlePlay: string
    appStore: string
  }
  knock: {
    feedId: string
    publicApiKey: string
  }
  customerIo: {
    siteId: string
  }
  openWeatherMapApiKey: string
  legalLinks: {
    privacyPolicy: string
    termsOfService: string
    termsAndConditions: string
  }
  localApiTunnelUrl?: string
  schedulingUrl: string
}

const LS_API_ENDPOINT = 'buildpass-api-endpoint'

const tryGetLocalStorageApiEndpoint = (defaultEndpoint: string): string => {
  const localStorageApiEndpoint = get<string>(LS_API_ENDPOINT)

  if (localStorageApiEndpoint) {
    return localStorageApiEndpoint
  }

  return defaultEndpoint
}

const rootConfig: Config = {
  name: 'buildpass-core-ui',
  version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'local',
  appHost: process.env.NEXT_PUBLIC_APP_URL
    ? process.env.NEXT_PUBLIC_APP_URL
    : `https://app.buildpass.com.au`,
  signInUrl: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL || '/sign-in',
  defaultPhoneCountryCode:
    process.env.NEXT_PUBLIC_DEFAULT_PHONE_COUNTRY_CODE || 'AU',
  supportEmail: 'support@buildpass.com.au',
  e2eEmail: 'hello+e2e@buildpass.com.au',
  environment,
  vercelEnvironment,
  service: process.env.NEXT_PUBLIC_SERVICE_NAME || 'core-ui',
  branch: process.env.VERCEL_GIT_COMMIT_REF || 'local',
  apiEndpoint: tryGetLocalStorageApiEndpoint(
    process.env.NEXT_PUBLIC_API_URL
      ? `${process.env.NEXT_PUBLIC_API_URL}/api/graphql`
      : 'https://api.buildpass.com.au/api/graphql'
  ),
  pdfGeneratorEndpoint: 'https://pdf-generator.buildpass.com.au/api/pdf',
  defaultLocale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en-AU',
  defaultTimezone:
    process.env.NEXT_PUBLIC_DEFAULT_TIMEZONE || 'Australia/Melbourne',
  defaultLanguage: process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || 'en',
  auth: {
    domain: 'auth.buildpass.com.au',
    clientId: 'DY3YU7BvCHqC1oNZhSXQBXevfWDiczP7',
    redirectPath: '/dashboards/overview',
    redirectUri: process.env.NEXT_PUBLIC_APP_URL
      ? `${process.env.NEXT_PUBLIC_APP_URL}/dashboards/overview`
      : 'https://app.buildpass.com.au/dashboards/overview',
    audience: 'https://buildpass.com.au/graphql',
  },
  redirectAppUrl: process.env.NEXT_PUBLIC_REDIRECT_APP_URL
    ? `${process.env.NEXT_PUBLIC_REDIRECT_APP_URL}/api`
    : 'https://redirect-app.buildpass.com.au/api',
  googleMapsAPIKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  appLinks: {
    googlePlay:
      'https://play.google.com/store/apps/details?id=au.com.buildpass.app',
    appStore: 'https://apps.apple.com/au/app/buildpass-aus/id1538577836',
  },
  knock: {
    feedId: '7a4284e8-100b-4c3a-b5e8-2ce9bf3759c4',
    publicApiKey: process.env.NEXT_PUBLIC_KNOCK_PUBLIC_API_KEY,
  },
  customerIo: {
    siteId: process.env.NEXT_PUBLIC_CUSTOMER_IO_SITE_ID,
  },
  openWeatherMapApiKey: process.env.NEXT_PUBLIC_OPEN_WEATHER_MAP_API_KEY,
  legalLinks: {
    privacyPolicy: 'https://buildpass.com.au/privacy-policy',
    termsAndConditions: 'https://buildpass.com.au/terms-and-conditions',
    termsOfService: 'https://buildpass.com.au/terms-of-service',
  },
  schedulingUrl: process.env.NEXT_PUBLIC_SCHEDULING_APP_URL,
}

const environmentConfigs = {
  dev: (): Partial<Config> => ({
    version: 'local',
    appHost: 'http://localhost:3000',
    apiEndpoint: tryGetLocalStorageApiEndpoint('http://localhost:8911/graphql'),
    pdfGeneratorEndpoint: 'http://localhost:2000/api/pdf',
    redirectAppUrl: 'http://localhost:3000/redirect',
    auth: {
      ...rootConfig.auth,
      domain: 'buildpass-staging.au.auth0.com',
      clientId: 'oK5MHR4YPjUvOPoks2xpclvCQKVx6EH4',
      redirectUri: 'http://localhost:3000/dashboards/overview',
    },
    localApiTunnelUrl: process.env.NEXT_PUBLIC_LOCAL_API_TUNNEL_URL,
  }),
  preview: (): Partial<Config> => {
    return {
      auth: {
        ...rootConfig.auth,
        domain: 'buildpass-staging.au.auth0.com',
        clientId: 'oK5MHR4YPjUvOPoks2xpclvCQKVx6EH4',
      },
      appHost: `https://${process.env.VERCEL_URL}`,
    }
  },
  production: (): Partial<Config> => ({}),
}

const config: Config = {
  ...rootConfig,
  ...environmentConfigs[environment](),
}

export default config
